import React, { useEffect, useState } from "react";
import { Cloud, CloudRain, Sun, CloudSnow, Wind, Loader2 } from "lucide-react";

const codeMap = (code) => {
  if (code === 0) return { label: "Clear", Icon: Sun };
  if ([1, 2].includes(code)) return { label: "Mostly sunny", Icon: Sun };
  if ([3].includes(code)) return { label: "Overcast", Icon: Cloud };
  if ([45, 48].includes(code)) return { label: "Foggy", Icon: Cloud };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 80, 81, 82].includes(code))
    return { label: "Rainy", Icon: CloudRain };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { label: "Snow", Icon: CloudSnow };
  if ([95, 96, 99].includes(code)) return { label: "Thunderstorm", Icon: CloudRain };
  return { label: "Cloudy", Icon: Cloud };
};

const WeatherWidget = ({ lat, lng }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    setLoading(true);
    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
    )
      .then((r) => r.json())
      .then((json) => {
        if (ok) setData(json.current);
      })
      .catch(() => {})
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [lat, lng]);

  if (loading) {
    return (
      <div className="bg-white border border-brand-border-warm rounded-2xl p-5 flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-brand-forest" />
        <span className="text-sm text-brand-ink/60">Fetching weather…</span>
      </div>
    );
  }
  if (!data) return null;

  const { label, Icon } = codeMap(data.weather_code);

  return (
    <div
      data-testid="weather-widget"
      className="bg-white border border-brand-border-warm rounded-2xl p-5 shadow-warm"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-brand-ink/50 font-semibold mb-3">
        Weather right now
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-heading text-4xl font-black text-brand-ink leading-none">
            {Math.round(data.temperature_2m)}°F
          </div>
          <div className="text-sm text-brand-ink/70 mt-1">{label}</div>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-brand-light-green text-brand-forest flex items-center justify-center">
          <Icon className="w-8 h-8" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-brand-border-warm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-ink/50 font-semibold">Wind</div>
          <div className="text-sm font-semibold text-brand-ink mt-0.5 flex items-center gap-1">
            <Wind className="w-3.5 h-3.5" /> {Math.round(data.wind_speed_10m)} mph
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-ink/50 font-semibold">Humidity</div>
          <div className="text-sm font-semibold text-brand-ink mt-0.5">
            {Math.round(data.relative_humidity_2m)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
